import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface ProtectedArtistRouteProps {
  children: React.ReactNode;
  requireApproval?: boolean;
}

const ProtectedArtistRoute = ({ children, requireApproval = true }: ProtectedArtistRouteProps) => {
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkArtistStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        setLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("user_type, approval_status")
        .eq("id", session.user.id)
        .single();

      if (!profile) {
        toast.error("Profil hittades inte");
        navigate("/");
        setLoading(false);
        return;
      }

      // Check if user is artist
      if (profile.user_type !== "artist") {
        toast.error("Denna sida är endast för konstnärer");
        navigate("/");
        setLoading(false);
        return;
      }

      // Check approval status if required
      if (requireApproval && profile.approval_status !== "approved") {
        toast.info("Din konstnärsprofil väntar fortfarande på godkännande");
        navigate("/profile");
        setLoading(false);
        return;
      }

      setIsAuthorized(true);
      setLoading(false);
    };

    checkArtistStatus();
  }, [navigate, requireApproval]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedArtistRoute;
